// React
import { useEffect, useRef } from 'react';

// Vendors
import moment from 'moment';
import jwtDecode from 'jwt-decode';

// Src
import { useAppSelector } from '@/infrastructure/redux/hooks/useAppSelector';
import { useAppDispatch } from '@/infrastructure/redux/hooks/useAppDispatch';
import { logoutRequestAction, updateAuthAction } from '../reducers/authenticationReducer';
import useRefreshTokenMutation from './useRefreshTokenMutation';
import { JWTAuthPayload } from '../types/authenticationTypes';

// Constants
const SESSION_ACTIVE_TIMEOUT_DURATION = 1 * 60 * 1000;

const useSessionManager = (): (() => void) => {
    // Hooks
    const refreshTokenMutation = useRefreshTokenMutation();
    const dispatch = useAppDispatch();
    const { loggedIn, loggedUserInfo } = useAppSelector((state) => state.authenticationReducer);
    const lastActiveTime = useRef<number>();

    // Handlers
    const handleSetActivityUserActive = (): void => {};

    const handleInactive = () => {
        lastActiveTime.current = Date.now();
    };

    const handleActive = () => {
        lastActiveTime.current = undefined;
    };

    const handleLogout = () => dispatch(logoutRequestAction());

    const handleRefreshToken = () => {
        if (!loggedUserInfo?.refresh_token) return handleLogout();
        if (lastActiveTime.current && Date.now() > lastActiveTime.current + SESSION_ACTIVE_TIMEOUT_DURATION) return handleLogout();

        refreshTokenMutation.mutate(
            {
                access_token: loggedUserInfo.access_token,
                refresh_token: loggedUserInfo.refresh_token,
            },
            {
                onSuccess: (result) => {
                    const nextLoggedUserInfo = result.data;
                    const authPayload = jwtDecode<JWTAuthPayload>(nextLoggedUserInfo.access_token);
                    dispatch(
                        updateAuthAction({
                            loggedIn: true,
                            loggedUserInfo: nextLoggedUserInfo,
                            userId: authPayload.userId,
                        })
                    );
                },
            }
        );
    };

    // Effects
    useEffect(() => {
        if (!loggedIn || !loggedUserInfo) return;

        const experationDate = jwtDecode<JWTAuthPayload>(loggedUserInfo.access_token).exp;

        if (experationDate * 1000 < Date.now()) {
            dispatch(logoutRequestAction());
            return;
        }

        const refreshInterval = moment.unix(experationDate).subtract(1, 'minutes').diff(moment());

        const timer = setTimeout(handleRefreshToken, refreshInterval);

        return () => {
            clearInterval(timer);
        };
    }, [loggedUserInfo]);

    useEffect(() => {
        window.addEventListener('focus', handleActive);
        window.addEventListener('blur', handleInactive);
        return () => {
            document.removeEventListener('focus', handleActive);
            document.removeEventListener('focus', handleInactive);
        };
    }, []);

    return handleSetActivityUserActive;
};

export default useSessionManager;
